import axios from 'axios';
import { useAuthStore } from '@/store/auth';
import router from '@/router';
import { useToast } from '@/components/ui/toast/use-toast'
const { toast } = useToast();

const BASE_URL = 'http://localhost:8000';
// const BASE_URL = 'https://app.unifood.unipad.pro';

const api = axios.create({
  baseURL: BASE_URL,
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};
// Добавляем токен в заголовки запросов, за исключением запросов на авторизацию
api.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore();
    const token = authStore.tokens?.access;
    // Проверка на исключение токена для авторизационных запросов
    const isAuthRequest = [
      '/auth/login',
      '/auth/request-password-reset',
      '/auth/reset-password',
      '/auth/refresh'
    ].some((url) =>
      config.url?.includes(url)
    );

    if (token && !isAuthRequest) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error)
  }
);

// Обрабатываем истечение токена

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const authStore = useAuthStore();

    // Если получили 401 и запрос ещё не был повторно отправлен
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        // Ждём, пока токен обновится
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
        .then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = authStore.tokens?.refresh; // Получить refresh token из хранилища

        if (!refreshToken) {
          throw new Error('Refresh token отсутствует');
        }

        // Отправляем запрос на обновление токена
        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, {
          refresh: refreshToken,
        });

        const newToken = data.access;
        const newRefreshToken = data.refresh;

        authStore.setTokens({ access: newToken, refresh: newRefreshToken });

        // Устанавливаем новый токен для повторного запроса
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

        processQueue(null, newToken);

        return api(originalRequest);
      } catch (err) {
        console.log('catch refresh', err);
        processQueue(err, null);
        authStore.clearTokens(); // Удаляем токены, если обновление не удалось
        router.push({ name: 'LoginView' }); // Перенаправляем на страницу входа
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    } else if (error.response) { //Если сервер вернул ошибку
      return Promise.reject(error);
    } else if (axios.isAxiosError(error)) { //Ошибка сети и тд.
      let errorStatus;
      let errorMessage;
      switch (error.code) {
        case 'ECONNREFUSED':
          errorStatus = 502;
          errorMessage = 'Сервер отказал в соединении (ECONNREFUSED)';
          break;
        case 'ENOTFOUND':
          errorStatus = 502;
          errorMessage = 'Сервер не найден (ENOTFOUND)';
          break;
        case 'ETIMEDOUT':
          errorStatus = 504;
          errorMessage = 'Время ожидания соединения истекло (ETIMEDOUT)';
          break;
        case 'ERR_NETWORK':
          errorStatus = 503;
          errorMessage = 'Сетевая ошибка (ERR_NETWORK)';
          break;
        default:
          errorStatus = 500;
          errorMessage = 'Неизвестная ошибка';
          break;
      }

      toast({
        title:'Упс! Что-то пошло не так!',
        description: errorMessage,
        variant:'destructive'
      })
      return Promise.reject({
        response:{
          status: errorStatus,
          data:{
            message: errorMessage
          }
        }
      });
    }
    return Promise.reject(error);
  }
);

export default api;
